/**
 * HomePage - ホーム画面コンポーネント
 */

import { useEffect } from 'react'
import { useStore, useAllSets, useCurrentSet } from '../store'
import type { AppMode } from '../core/types'
import { APP_VERSION, BUILD_TIME, COMMIT_HASH } from '../version'
import { generateBackgroundGradient } from '../utils/colorGenerator'

export function HomePage() {
  const setsMap = useAllSets()
  const sets = Object.values(setsMap)
  const currentSet = useCurrentSet()
  const navigateTo = useStore(state => state.navigateTo)
  
  // BUILD_TIMEをローカル時刻に変換
  const getLocalBuildTime = () => {
    try {
      // ISO形式かチェック（末尾のZはUTCを示す）
      if (BUILD_TIME.includes('T')) {
        const date = new Date(BUILD_TIME)
        // 日本時間で表示（ブラウザのロケールに依存）
        return date.toLocaleString('ja-JP', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'Asia/Tokyo'
        })
      }
      // 現在の形式 "YYYY-MM-DD HH:mm" を解析（後方互換性のため）
      const parts = BUILD_TIME.split(' ')
      if (parts.length === 2) {
        const [datePart, timePart] = parts
        const dateParts = datePart?.split('-') || []
        const timeParts = timePart?.split(':') || []
        if (dateParts.length === 3 && timeParts.length === 2) {
          const [year, month, day] = dateParts
          const [hour, minute] = timeParts
          const buildDate = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute))
          return buildDate.toLocaleString('ja-JP')
        }
      }
      return BUILD_TIME
    } catch {
      return BUILD_TIME // 変換に失敗した場合は元の値を返す
    }
  }

  // アプリ終了確認の処理
  useEffect(() => {
    // ブラウザの閉じる・リロード時の確認
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // ブラウザ標準の確認ダイアログを表示
      e.preventDefault()
      e.returnValue = '本当にアプリを終了しますか？'
      return '本当にアプリを終了しますか？'
    }
    
    // バックボタンの処理
    let confirmationShown = false
    const handlePopState = (e: PopStateEvent) => {
      if (!confirmationShown) {
        e.preventDefault()
        confirmationShown = true
        
        // 確認ダイアログを表示
        if (confirm('本当にアプリを終了しますか？')) {
          // ユーザーが確認したら履歴を1つ戻す（アプリを閉じる）
          window.history.back()
        } else {
          // キャンセルしたら履歴エントリを再追加
          window.history.pushState({ page: 'home' }, '', null)
          confirmationShown = false
        }
      }
    }
    
    // 初回エントリを追加（バックボタン検知用）
    window.history.pushState({ page: 'home' }, '', null)
    
    // イベントリスナー登録
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('popstate', handlePopState)
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  const handleNavigate = (mode: AppMode) => {
    navigateTo(mode)
  }

  // コミットハッシュから背景グラデーションを生成
  const backgroundGradient = generateBackgroundGradient(COMMIT_HASH)

  return (
    <div 
      className="min-h-screen text-white flex items-center justify-center"
      style={{ background: backgroundGradient }}
    >
      <div className="max-w-md w-full px-6">
        <h1 className="text-4xl font-bold text-center mb-8">
          ParaParaView
          <span className="text-sm text-gray-400 block mt-2">v{APP_VERSION} ({COMMIT_HASH})</span>
          <span className="text-xs text-gray-500 block mt-1">{getLocalBuildTime()}</span>
        </h1>
        
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => handleNavigate('capture')}
            className="p-6 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            <svg className="w-8 h-8 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            撮影
          </button>
          
          <button
            onClick={() => handleNavigate('decks')}
            className="p-6 bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors"
          >
            <svg className="w-8 h-8 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            デッキ
          </button>
          
          <button
            onClick={() => handleNavigate('graph')}
            className="p-6 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
          >
            <svg className="w-8 h-8 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            グラフ
          </button>
          
          <button
            onClick={() => handleNavigate('settings')}
            className="p-6 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
          >
            <svg className="w-8 h-8 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            設定
          </button>
        </div>
        
        {/* ImageSet Info */}
        <div className="mt-8 text-center text-gray-400 text-sm">
          <p>セット数: {sets.length}</p>
          {currentSet && (
            <p>現在のセット: {currentSet.name} ({currentSet.type})</p>
          )}
        </div>
      </div>
    </div>
  )
}